import { useCallback } from "react";
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";

/* -------------------------------------------------------------------
   📌 useExcelDownload Hook Documentation

   ✅ How to Import:
      import useExcelDownload from './useExcelDownload';

   ✅ How to Use:
      const downloadExcel = useExcelDownload();

      // Call the function wherever needed
      downloadExcel(flatData, "Fleet Graph and Insight Data");

   ✅ Parameters:
      downloadExcel(data, fileName);

      1. data     → (Array of Objects) 
                    Each object represents a row in the Excel sheet.
      2. fileName → (String) 
                    The name of the Excel file to be downloaded 
                    (without extension).

   ✅ Example Data Format:
      const formData = [
        {
          "Vin No": "MH10ED4540",
          "Value": 20
        },
        {
          "Vin No": "MH10BL4540",
          "Value": 30
        }
      ];

   ✅ Example Usage:
      downloadExcel(formData, "Vehicle Data");

   📎 Notes:
      - The keys of each object will become the column headers.
      - Each object in the array will be a new row in the Excel file.
      - The exported file will automatically have a `.xlsx` extension.

   ------------------------------------------------------------------- */

const useExcelDownload = () => {
    const downloadExcel = useCallback((flatData, fileName = "Fleet Report") => {
        if (!flatData || !Array.isArray(flatData) || flatData.length === 0) {
            console.warn("No flatData provided to download Excel.");
            return;
        }

        const fileType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        const fileExtension = ".xlsx";

        const worksheet = XLSX.utils.json_to_sheet(flatData);
        const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

        const blob = new Blob([excelBuffer], { type: fileType });
        FileSaver.saveAs(blob, `${fileName}${fileExtension}`);
    }, []);

    return downloadExcel;
};

export const useCsvBlob = () => {
    const getCsvBlob = useCallback((flatData, fileName = "Fleet Report") => {
        if (!flatData || !Array.isArray(flatData) || flatData.length === 0) {
            console.warn("No flatData provided to generate CSV.");
            return null;
        }

        // 1. Convert JSON to worksheet
        const worksheet = XLSX.utils.json_to_sheet(flatData);

        // 2. Convert worksheet to CSV
        const csv = XLSX.utils.sheet_to_csv(worksheet);

        // 3. Create Blob from CSV
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

        // 4. Optional: return both blob and file name
        return {
            blob,
            file: new File([blob], `${fileName}.csv`, { type: "text/csv" }),
        };
    }, []);

    return getCsvBlob;
};


export default useExcelDownload;